import { useEffect, useRef, useState } from 'react';

/**
 * Pinned page title. Every feature page opens with an eyebrow, a large title
 * and a short description. Once that header scrolls up under the top bar, a
 * compact copy of it (eyebrow · title, plus the description on one line)
 * slides in, pinned just below the top bar over the page column, so the member
 * always knows which feature they're in. The original header scrolls normally,
 * so nothing in the page moves or jumps. Tapping the bar scrolls back to top.
 *
 * Generic on purpose: pages don't opt in. It reads the first <h1> inside
 * <main>, the short line just above it (the eyebrow) and the paragraph just
 * below it (the description), and re-reads when the page renders late.
 */
export function PageTitleBar({ routeKey, topOffset }: { routeKey: string; topOffset: number }) {
  const [head, setHead] = useState<{ eyebrow: string; title: string; desc: string } | null>(null);
  const [shown, setShown] = useState(false);
  const h1Ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setShown(false);
    setHead(null);
    h1Ref.current = null;
    const main = document.querySelector('.shell__main main');
    if (!main) return;

    let io: IntersectionObserver | null = null;

    const read = () => {
      const h1 = main.querySelector<HTMLElement>('h1');
      if (!h1 || !h1.textContent?.trim()) return;
      const title = h1.textContent.trim();
      // Eyebrow: the short line directly above the title, if any.
      const prev = h1.previousElementSibling as HTMLElement | null;
      const eyebrowText = prev?.textContent?.trim() ?? '';
      const eyebrow = eyebrowText && eyebrowText.length <= 90 ? eyebrowText : '';
      // Description: the paragraph directly below the title, if any.
      const next = h1.nextElementSibling as HTMLElement | null;
      const desc = next && next.tagName === 'P' ? (next.textContent ?? '').trim() : '';

      setHead((cur) =>
        cur && cur.title === title && cur.eyebrow === eyebrow && cur.desc === desc ? cur : { eyebrow, title, desc },
      );

      if (h1Ref.current !== h1) {
        h1Ref.current = h1;
        io?.disconnect();
        io = new IntersectionObserver(
          ([entry]) => {
            // Show once the title has scrolled up past the top bar.
            setShown(!entry.isIntersecting && entry.boundingClientRect.top < topOffset);
          },
          { rootMargin: `-${topOffset}px 0px 0px 0px`, threshold: 0 },
        );
        io.observe(h1);
      }
    };

    read();
    // Pages often render their header after data arrives; keep watching.
    const mo = new MutationObserver(() => read());
    mo.observe(main, { childList: true, subtree: true, characterData: true });
    return () => {
      mo.disconnect();
      io?.disconnect();
    };
  }, [routeKey, topOffset]);

  if (!head) return null;

  return (
    <div
      className={`ptbar${shown ? ' is-shown' : ''}`}
      style={{ top: topOffset }}
      aria-hidden={!shown}
    >
      <button
        type="button"
        className="ptbar__inner"
        tabIndex={shown ? 0 : -1}
        title="Back to top"
        onClick={() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          document.body.scrollTo?.({ top: 0, behavior: 'smooth' });
        }}
      >
        {head.eyebrow && <span className="ptbar__eyebrow">{head.eyebrow}</span>}
        <span className="ptbar__title">{head.title}</span>
        {head.desc && <span className="ptbar__desc">{head.desc}</span>}
      </button>
    </div>
  );
}
